import * as THREE from 'three';
import { WebGPURenderer } from 'three/webgpu';

// Which rendering path to use. "legacy" is the WebGLRenderer the game shipped with; "new" is
// three.js's WebGPURenderer on its WebGL2 backend, taking WebGPU only on the high setting (measured
// 7 October 2026: on an ordinary laptop GPU the WebGPU backend was 25-35 % slower than WebGL2 for this
// scene); "new-gpu" forces WebGPU and "new-gl" forces WebGL2, for testing either.
// The choice is remembered in the browser so a phone need not carry it in the address.
export function gfxMode(params) {
  const asked = params.get('gfx');
  let mode = asked;
  try {
    if (asked) localStorage.setItem('abakwa-gfx', asked);
    else mode = localStorage.getItem('abakwa-gfx');
  } catch {
    // no storage: the address decides
  }
  return mode === 'new' || mode === 'new-gl' || mode === 'new-gpu' ? mode : 'legacy';
}

// Node materials (TSL) are needed for everything the new renderer draws with custom shading.
export const usesNodes = (mode) => mode !== 'legacy';

// The colour grade of the art bible, applied inside the tone mapping every material already does
// (no extra pass, no cost): ACES, then a little less saturation, more contrast, warm lights and
// cool shadows, as the reference frames have. Legacy renderer only; the node renderer keeps ACES.
let graded = false;
function installGrade() {
  if (graded) return;
  graded = true;
  const plain = 'vec3 CustomToneMapping( vec3 color ) { return color; }';
  const chunk = THREE.ShaderChunk.tonemapping_pars_fragment;
  if (!chunk.includes(plain)) return;
  THREE.ShaderChunk.tonemapping_pars_fragment = chunk.replace(
    plain,
    `vec3 CustomToneMapping( vec3 color ) {
      color = ACESFilmicToneMapping( color );
      // graded in gamma space, where mid-grey is 0.5; back to linear for the output transform
      vec3 g = pow( color, vec3( 1.0 / 2.2 ) );
      float l = dot( g, vec3( 0.2126, 0.7152, 0.0722 ) );
      g = mix( vec3( l ), g, 0.88 );
      g = ( g - 0.5 ) * 1.08 + 0.5;
      g += ( l - 0.5 ) * vec3( 0.06, 0.02, -0.06 );
      return pow( clamp( g, 0.0, 1.0 ), vec3( 2.2 ) );
    }`
  );
}

// Makes the renderer for the chosen mode, ready to draw. quality: { pixelRatio, shadows, webgpu }.
// grade: false keeps plain ACES (?grade=0), for comparing.
export async function createRenderer(canvas, quality, mode, grade = true) {
  let renderer;
  if (mode === 'legacy') {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
    if (grade) installGrade();
  } else {
    const wantGpu = mode === 'new-gpu' || (mode === 'new' && quality.webgpu);
    renderer = new WebGPURenderer({ canvas, antialias: true, forceWebGL: !wantGpu, trackTimestamp: true });
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, quality.pixelRatio));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = mode === 'legacy' && grade ? THREE.CustomToneMapping : THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.3;
  renderer.shadowMap.enabled = quality.shadows;
  renderer.shadowMap.type = quality.softShadows ? THREE.PCFSoftShadowMap : THREE.PCFShadowMap;
  if (renderer.init) await renderer.init();
  return renderer;
}

// A short name for what is actually drawing: "webgl" (legacy), "webgpu" or "webgl2" (new renderer).
export function backendName(renderer, mode) {
  if (mode === 'legacy') return 'webgl';
  return renderer.backend && renderer.backend.isWebGPUBackend ? 'webgpu' : 'webgl2';
}
